import {filterArrayNull} from '@coolcolduk/util';
import type {DocumentData, Query, QueryDocumentSnapshot, QuerySnapshot} from 'firebase/firestore';
import {getDocs} from 'firebase/firestore';
import {useCallback, useEffect, useRef, useState} from 'react';
import {env} from '../../config/env';
import mapFirestoreDocToObjectType from '../../helper/firebase/mapFirestoreDocToObjectType';
import type {WithId} from '../../types/WithId';

/** Cursor for next page (startAfter) or previous page (endBefore). */
export type FirestorePaginationCursor<TApp = DocumentData, TDb extends DocumentData = DocumentData> =
  | {startAfter: QueryDocumentSnapshot<TApp, TDb>}
  | {endBefore: QueryDocumentSnapshot<TApp, TDb>}
  | null;

export interface UseFirestorePaginationOptions<TApp = DocumentData, TDb extends DocumentData = DocumentData> {
  /** Build the Firestore query for the current cursor. Use startAfter/endBefore for next/prev. */
  buildQuery: (cursor: FirestorePaginationCursor<TApp, TDb>) => Query<TApp, TDb>;
  /** Page size. Used for canGoNext (true when current page is full). Default 10. */
  limit?: number;
  /** Map each document to app model. Defaults to mapFirestoreDocToObjectType. */
  mapDoc?: (doc: QueryDocumentSnapshot<TApp, TDb>) => WithId<TApp> | null;
}

export interface UseFirestorePaginationResult<TApp, TDb extends DocumentData = DocumentData> {
  /** Current page data (app model with id). */
  data: WithId<TApp>[];
  /** Set data from an external snapshot (e.g. from a listener). Maps docs and updates state. */
  setData: (snapshot: QuerySnapshot<TApp, TDb>) => void;
  /** First document of the current page. */
  firstDoc: QueryDocumentSnapshot<TApp, TDb> | null;
  /** Last document of the current page. */
  lastDoc: QueryDocumentSnapshot<TApp, TDb> | null;
  /** Current page index (0-based). */
  pageIndex: number;
  /** Fetch next page (uses lastDoc). */
  handleNextPage: () => void;
  /** Fetch previous page (uses firstDoc). */
  handlePrevPage: () => void;
  /** Go back to first page. */
  handleFirstPage: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Generic Firestore cursor-based pagination hook.
 * - TApp: app/model type (data returned as WithId<TApp>[]).
 * - TDb: Firestore document type (default DocumentData). Use when collection has a converter.
 * - Runs buildQuery(cursor), maps snapshot with mapFirestoreDocToObjectType (or custom mapDoc).
 * - setData(snapshot) lets the caller inject a snapshot to update data (e.g. from a listener).
 */
const defaultMapDoc = <TApp, TDb extends DocumentData>(d: QueryDocumentSnapshot<TApp, TDb>): WithId<TApp> | null =>
  mapFirestoreDocToObjectType<TApp, TDb>(d) as WithId<TApp> | null;

export function useFirestorePagination<TApp = DocumentData, TDb extends DocumentData = DocumentData>(
  options: UseFirestorePaginationOptions<TApp, TDb>,
): UseFirestorePaginationResult<TApp, TDb> {
  const {buildQuery, limit = 10, mapDoc = defaultMapDoc} = options;

  const [cursor, setCursor] = useState<FirestorePaginationCursor<TApp, TDb>>(null);
  const [data, setDataState] = useState<WithId<TApp>[]>([]);
  const [firstDoc, setFirstDoc] = useState<QueryDocumentSnapshot<TApp, TDb> | null>(null);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<TApp, TDb> | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const cancelledRef = useRef(false);

  const setData = useCallback(
    (snapshot: QuerySnapshot<TApp, TDb>) => {
      const list = filterArrayNull(snapshot.docs.map((d) => mapDoc(d)));
      setDataState(list);
      setFirstDoc(snapshot.docs[0] ?? null);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] ?? null);
    },
    [mapDoc],
  );

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = buildQuery(cursor);
      const snapshot = await getDocs(q);
      if (cancelledRef.current) return;
      setData(snapshot);
    } catch (err) {
      if (!cancelledRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setDataState([]);
        setFirstDoc(null);
        setLastDoc(null);
      }
      if (env.isDev) console.error('Error fetching Firestore pagination:', err);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [buildQuery, cursor, setData]);

  useEffect(() => {
    cancelledRef.current = false;
    fetch();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetch]);

  const handleNextPage = useCallback(() => {
    if (!lastDoc) return;
    setCursor({startAfter: lastDoc});
    setPageIndex((i) => i + 1);
  }, [lastDoc]);

  const handlePrevPage = useCallback(() => {
    if (!firstDoc) return;
    setCursor({endBefore: firstDoc});
    setPageIndex((i) => Math.max(0, i - 1));
  }, [firstDoc]);

  const handleFirstPage = useCallback(() => {
    setCursor(null);
    setPageIndex(0);
  }, []);

  const canGoNext = lastDoc != null && data.length === limit;
  const canGoPrev = pageIndex > 0;

  return {
    data,
    setData,
    firstDoc,
    lastDoc,
    pageIndex,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    canGoNext,
    canGoPrev,
    loading,
    error,
    refetch: fetch,
  };
}
